// Campañas de pauta y anuncios (Marketing M1-2): lectura y cambios uno a uno.
import type { SupabaseClient } from '@supabase/supabase-js';
import { COLS_ANUNCIO, COLS_CAMPANA, armarCampanas } from './pedidos';
import type { Anuncio, Campana } from '../logica/campanas';

const claro = (m: string) => (/duplicate key|unique/i.test(m) ? 'Ese código ya lo usa otra campaña o anuncio.' : /fechas_en_orden/.test(m) ? 'La fecha final no puede ser antes de la inicial.' : m);
const ok = ({ error }: { error: { message: string } | null }) => { if (error) throw new Error(claro(error.message)); };

export async function cargarCampanas(sb: SupabaseClient): Promise<Campana[]> {
  const [c, a] = await Promise.all([sb.from('campanas_pauta').select(COLS_CAMPANA).order('id', { ascending: false }), sb.from('anuncios').select(COLS_ANUNCIO).order('id')]);
  const e = c.error || a.error;
  if (e) throw new Error(e.message);
  return armarCampanas(c.data || [], a.data || []);
}
export async function crearCampana(sb: SupabaseClient, fila: Pick<Campana, 'codigo' | 'nombre'> & Partial<Campana>): Promise<number> {
  const { data, error } = await sb.from('campanas_pauta').insert(fila).select('id').single();
  if (error) throw new Error(claro(error.message));
  return data.id as number;
}
export async function cambiarCampana(sb: SupabaseClient, id: number, cambios: Partial<Omit<Campana, 'id' | 'anuncios'>>) {
  ok(await sb.from('campanas_pauta').update(cambios).eq('id', id));
}
export async function crearAnuncio(sb: SupabaseClient, fila: Pick<Anuncio, 'campana_id' | 'codigo' | 'nombre'>): Promise<Anuncio> {
  const { data, error } = await sb.from('anuncios').insert(fila).select(COLS_ANUNCIO).single();
  if (error) throw new Error(claro(error.message));
  return data as Anuncio;
}
export async function cambiarAnuncio(sb: SupabaseClient, id: number, cambios: Partial<Omit<Anuncio, 'id' | 'campana_id'>>) {
  ok(await sb.from('anuncios').update(cambios).eq('id', id));
}
export async function borrarAnuncio(sb: SupabaseClient, id: number) {
  const { count, error } = await sb.from('pedidos').select('id', { count: 'exact', head: true }).eq('anuncio_id', id);
  if (error) throw new Error(error.message);
  if (count) throw new Error('Este anuncio ya tiene pedidos: no se borra. Desactívalo para que no aparezca en los pedidos nuevos.');
  ok(await sb.from('anuncios').delete().eq('id', id));
}

// Una campaña se borra solo si no tiene pedidos ni cotizaciones (sus anuncios se borran con ella).
export async function borrarCampana(sb: SupabaseClient, id: number) {
  const { count, error } = await sb.from('pedidos').select('id', { count: 'exact', head: true }).eq('pauta_id', id);
  if (error) throw new Error(error.message);
  if (count) throw new Error('Esta campaña ya tiene pedidos o cotizaciones: no se borra, para no perder de dónde vinieron. Márcala como "Terminada".');
  ok(await sb.from('campanas_pauta').delete().eq('id', id));
}

// WhatsApp del negocio leído de la base (no de la configuración en memoria, que se recarga con un pequeño retraso).
export async function cargarWhatsAppNegocio(sb: SupabaseClient): Promise<string> {
  const { data, error } = await sb.from('parametros').select('valor').eq('clave', 'whatsapp_negocio').maybeSingle();
  if (error) throw new Error(error.message);
  return (data?.valor as { numero?: string } | null)?.numero || '';
}
